<template>
  <div class="overview-container">
    <!-- Header Section -->
    <div class="header-bg">
      <div class="decorative-circle circle-1"></div>
      <div class="decorative-circle circle-2"></div>
      <div class="header-content">
        <div class="user-info">
          <div class="avatar" @click="showUserInfo = true">
            <van-icon name="manager" size="24" color="#333" />
          </div>
          <span class="title">SUPER REP</span>
        </div>
        <div v-if="roleActions.length > 1" class="role-switcher" @click="showRoleSelector = true">
          <span class="role-text">{{ currentRoleDisplay }}</span>
          <van-icon name="exchange" size="16" />
        </div>
      </div>
    </div>

    <div class="recommendations-section" style="padding: 0 12px; margin-top: -140px; position: relative; z-index: 1;">
      <TaskSummary
        :total-count="totalTaskCount"
        :completed-count="completedTaskCount"
        :events="eventsList"
        :is-loading="isLoadingTasks"
        :user-id="userId"
        @go-tasks="goToTasks"
        @go-detail="goToDoctorDetail"
        @plan-visit="planVisit"
        @fill-survey="handleFillSurvey"
        @refresh="loadTaskStats"
      />

      <!-- Section 3: Insights & Suggestions -->
      <div v-if="currentRole === '销售' || currentRole === 'MR' || currentRole === 'GBA' || currentRole === 'GA'" class="middle-section">
        <!-- AI Suggestion (Left) -->
        <div class="info-card suggestion-card" @click="showRecommendations = true">
          <div class="card-header">
            <span>{{ $t('kpi_completion') }}</span>
            <van-icon name="arrow" color="#999" />
          </div>
          <p class="kpi-hint">{{ $t('kpi_tap_to_view') }}</p>
          <ul class="kpi-bullet-list">
            <li class="kpi-item-green">{{ $t('kpi_section_customer_development') }}</li>
            <li class="kpi-item-blue">{{ $t('kpi_section_digital_interaction') }}</li>
            <li class="kpi-item-orange">{{ $t('kpi_section_behavior_process') }}</li>
          </ul>
        </div>

        <!-- Doctor Insights (Right) -->
        <div class="info-card insight-card" @click="askInsights">
          <div class="card-header">
            <span>{{ $t('recent_updates') }}</span>
            <van-icon v-if="!recentUpdatesLoading" name="arrow" color="#999" />
          </div>
          <div v-if="recentUpdatesLoading && recentUpdatesSummary.length === 0" class="insight-loading">
            <div class="ai-bubble">
              <div class="typing-indicator">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
              </div>
            </div>
          </div>
          <ul v-else class="insight-list">
            <li v-for="(item, index) in (recentUpdatesSummary.length ? recentUpdatesSummary : [$t('no_recent_updates')])" :key="index">
              <span class="bullet">●</span>
              <span class="text">{{ item }}</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Section 2: Visit Strategy -->
      <NbaSuggestions
        @go-doctor-detail="goToDoctorDetail"
        @ask-ai-helper="askAIHelper"
        @go-create-visit="goToCreateVisit"
        @go-send-content="goToSendContent"
      />

    </div>

    <!-- Spacer for TabBar -->
    <div style="height: 110px;"></div>

    <!-- Role Selector -->
    <van-action-sheet
      v-model="showRoleSelector"
      :actions="roleActions"
      cancel-text="取消"
      close-on-click-action
      description="切换当前身份"
      @select="onSelectRole"
    />

    <!-- Recommendations Popup -->
    <van-popup v-model="showRecommendations" position="right" :style="{ width: '100%', height: '100%' }">
      <KPIAnalysis @close="showRecommendations = false" />
    </van-popup>

    <!-- Recent Updates Popup -->
    <van-popup v-model="showRecentUpdates" position="right" :style="{ width: '100%', height: '100%' }">
      <RecentUpdates
        :user-id="userId"
        :role-id="roleId"
        :cached-text="recentUpdatesText"
        @close="showRecentUpdates = false"
        @summary-updated="handleRecentSummaryUpdated"
      />
    </van-popup>

    <!-- User Info Popup -->
    <van-popup v-model="showUserInfo" round position="center" :style="{ width: '80%', padding: '20px', borderRadius: '12px' }">
      <div class="user-info-card">
        <div class="info-header">
          <div class="avatar-large">
            <van-icon name="manager" size="40" color="#fff" />
          </div>
          <div class="info-title">当前用户信息</div>
        </div>
        <div class="info-content">
          <div class="info-item">
            <span class="label">用户名称</span>
            <span class="value">{{ userName }}</span>
          </div>
          <div class="info-item">
            <span class="label">用户ID</span>
            <span class="value">{{ userId }}</span>
          </div>
          <div class="info-item">
            <span class="label">当前角色</span>
            <span class="value">{{ currentRole }}</span>
          </div>
          <div class="info-item">
            <span class="label">语言/Language</span>
            <div class="language-switch">
              <span :class="['lang-option', { active: currentLanguage === 'ZH' }]" @click="switchLanguage('ZH')">中文</span>
              <span class="divider">|</span>
              <span :class="['lang-option', { active: currentLanguage === 'EN' }]" @click="switchLanguage('EN')">English</span>
            </div>
          </div>
        </div>
        <div class="info-footer">
          <van-button type="info" block round color="#aa341b" @click="showUserInfo = false">
            关闭
          </van-button>
        </div>
      </div>
    </van-popup>

    <!-- Dialogs for Task Actions -->
    <BrintellixTypingDialog
      :show="showTypingDialog"
      :physician-id="typingDialogTask ? typingDialogTask.physician_id : ''"
      :physician-name="typingDialogTask ? typingDialogTask.physician_name : ''"
      :product-id="typingDialogTask ? typingDialogTask.product_id : ''"
      :initial-answers="typingDialogAnswers"
      @update:show="showTypingDialog = $event"
      @close="showTypingDialog = false"
      @finish="onTypingFinish"
    />

    <SurveyDialog
      :show="showSurveyDialog"
      :questionnaire-id="surveyDialogTask ? surveyDialogTask.questionnaire_id : ''"
      :physician-id="surveyDialogTask ? surveyDialogTask.physician_id : ''"
      :physician-name="surveyDialogTask ? surveyDialogTask.physician_name : ''"
      :recorder-id="userId"
      :recorder-name="userName"
      @update:show="showSurveyDialog = $event"
      @close="showSurveyDialog = false"
      @finish="onSurveyFinish"
    />
  </div>
</template>

<script>
import { Icon, Button, Popup, ActionSheet, Toast, Loading } from 'vant';
import KPIAnalysis from './components/KPIAnalysis.vue';
import TaskSummary from './components/TaskSummary.vue';
import NbaSuggestions from './components/NbaSuggestions.vue';
import { executeTask, getMyCampaignEventStat } from '@/api/nba';
import { streamMyHCPUpdates, getUserRecentHcpUpdateSummary, saveUserRecentHcpUpdateSummary } from '@/api/ai';
import { getCnfRoles, switchRole } from '@/api/user';
import { isGBA, getLocalStorage, setLocalStorage, isEnableAI, resolveEnableAI } from '@/utils/index';
import { setToken } from '@/utils/auth';
import moment from 'moment';
import RecentUpdates from './components/RecentUpdates.vue';
import BrintellixTypingDialog from './components/NBA/BrintellixTypingDialog.vue';
import SurveyDialog from './components/NBA/SurveyDialog.vue';

export default {
  name: 'Overview',
  components: {
    [Icon.name]: Icon,
    [Button.name]: Button,
    [Popup.name]: Popup,
    [ActionSheet.name]: ActionSheet,
    [Loading.name]: Loading,
    KPIAnalysis,
    TaskSummary,
    RecentUpdates,
    NbaSuggestions,
    BrintellixTypingDialog,
    SurveyDialog
  },
  data() {
    return {
      isLoadingTasks: false,
      userId: getLocalStorage('user_id') || '',
      roleId: getLocalStorage('role_id') || '',
      userName: getLocalStorage('user_name') || '',
      totalTaskCount: 0,
      completedTaskCount: 0,
      eventsList: [],
      showRecommendations: false,
      showRecentUpdates: false,
      showRoleSelector: false,
      showUserInfo: false,
      currentRole: getLocalStorage('role_name') || 'MR',
      currentLanguage: getLocalStorage('language') || 'EN',
      roleActions: [],
      recentUpdatesLoading: false,
      recentUpdatesSummary: [],
      recentUpdatesText: '',
      defaultAvatar: require('@/assets/lundbeck/avatar-unknown.png'),
      showTypingDialog: false,
      typingDialogTask: null,
      typingDialogAnswers: null,
      showSurveyDialog: false,
      surveyDialogTask: null
    };
  },
  computed: {
    currentRoleDisplay() {
      return this.currentRole === '销售' ? 'MR' : this.currentRole;
    }
  },
  created() {
    if (!isEnableAI()) {
      this.$router.replace('/hcp/list');
      return;
    }
    this.fetchRoleOptions();
    this.loadTaskStats();
    this.loadRecentUpdatesSummary();
  },
  methods: {
    parseRecentSummary(text) {
      if (!text) return [];
      const cleanText = text.replace(/^```[a-zA-Z]*\s*/, '').replace(/\s*```$/, '').trim();

      const summaryHeaderRegex = /【\s*(?:近期动态汇总|Recent (?:Dynamic(?:s)?|Activity) Summary)\s*】/i;
      const nextSectionHeaderRegex = /【\s*(?:近期动态详情|Recent (?:Dynamic(?:s)?|Activity) Detail(?:s)?|下一步行动建议|Next (?:Action Suggestions|Step Recommendations))\s*】/i;

      const summarySplit = cleanText.split(summaryHeaderRegex);
      const summaryPart = summarySplit.length > 1 ? summarySplit[1] : '';
      const summaryOnly = summaryPart.split(nextSectionHeaderRegex)[0] || '';

      const lines = summaryOnly
        .split('\n')
        .map(line => line.trim())
        .filter(line => line);

      return lines
        .map((line) => {
          const withoutBullet = line.replace(/^[-•*]\s*/, '').trim();
          return withoutBullet.replace(/^\d+\.\s+/, '').trim();
        })
        .filter(line => line);
    },
    async loadRecentUpdatesSummary() {
      if (!this.userId) return;
      const roleId = getLocalStorage('role_id') || '';

      // Check Cache
      try {
        const res = await getUserRecentHcpUpdateSummary(this.userId, roleId);
        if (res && res.value && res.value.length > 0) {
          const item = res.value[0];
          const createTime = moment(item.create_time);
          if (moment().diff(createTime, 'days') < 3) {
            const summary = item.summary;
            if (summary) {
              this.recentUpdatesText = summary;
              this.recentUpdatesSummary = this.parseRecentSummary(summary);
              return;
            }
          }
        }
      } catch (e) {
        console.error('Check recent updates cache failed', e);
      }

      this.recentUpdatesLoading = true;
      this.recentUpdatesSummary = [];
      this.recentUpdatesText = '';

      let buffer = '';
      let accumulatedText = '';

      try {
        await streamMyHCPUpdates({
          data: { content: `user_id=${this.userId}&today=${moment().format('YYYY-MM-DD')}` },
          onChunk: (chunk) => {
            buffer += chunk;
            const lines = buffer.split('\n');
            buffer = lines.pop();

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed.startsWith('data:')) continue;
              const jsonStr = trimmed.substring(5).trim();
              if (!jsonStr) continue;

              try {
                const json = JSON.parse(jsonStr);
                if (json.output && typeof json.output.text === 'string') {
                  const newText = json.output.text;
                  if (accumulatedText && newText.startsWith(accumulatedText)) {
                    accumulatedText = newText;
                  } else {
                    accumulatedText += newText;
                  }
                  this.recentUpdatesText = accumulatedText;
                  this.recentUpdatesSummary = this.parseRecentSummary(accumulatedText);
                }
              } catch (e) {
                // Ignore JSON parse errors
              }
            }
          },
          onComplete: () => {
            this.recentUpdatesLoading = false;
            if (accumulatedText) {
              this.recentUpdatesText = accumulatedText;
              this.recentUpdatesSummary = this.parseRecentSummary(accumulatedText);
              // Save Cache
              saveUserRecentHcpUpdateSummary({
                user_id: this.userId,
                role_id: roleId,
                summary: accumulatedText,
                create_time: moment().format('YYYY-MM-DD HH:mm:ss')
              }).catch(err => console.error('Save cache failed', err));
            }
          },
          onError: () => {
            this.recentUpdatesLoading = false;
          }
        });
      } catch (e) {
        this.recentUpdatesLoading = false;
      }
    },
    openRecentUpdates() {
      this.showRecentUpdates = true;
    },
    handleRecentSummaryUpdated({ summaryBullets, rawText }) {
      this.recentUpdatesSummary = summaryBullets || [];
      this.recentUpdatesText = rawText || this.recentUpdatesText;
    },
    async loadTaskStats() {
      this.isLoadingTasks = true;
      try {
        const res = await getMyCampaignEventStat();
        const stat = res && res.data ? res.data : {};
        const campaignEvents = Array.isArray(stat.campaign_events) ? stat.campaign_events : [];

        const events = campaignEvents.map(event => {
          let topTasks = Array.isArray(event.top_tasks) ? event.top_tasks : [];

          if (!topTasks.length && event.top_tasks_json) {
            try {
              const parsedTasks = JSON.parse(event.top_tasks_json);
              topTasks = Array.isArray(parsedTasks) ? parsedTasks : [];
            } catch (e) {
              topTasks = [];
            }
          }

          return {
            ...event,
            doctorCount: event.unexecuted_task_count || topTasks.length || 0,
            topTasks,
            topTask: topTasks[0] || null
          };
        }).filter(event => event.doctorCount > 0);

        this.eventsList = events;
        this.completedTaskCount = stat.accept_task_count || 0;
        this.totalTaskCount = stat.total_task_count || 0;
        this.$root.$emit('updateTaskCount', stat.unexecuted_task_count || 0);
      } catch (error) {
        console.error('Failed to load task stats:', error);
      } finally {
        this.isLoadingTasks = false;
      }
    },
    handleFillSurvey(task) {
      if (task.event_type === 'survey') {
        this.surveyDialogTask = task;
        this.showSurveyDialog = true;
        return;
      }
      if (task.event_type === 'segment') {
        this.typingDialogTask = task;
        this.typingDialogAnswers = null;
        this.showTypingDialog = true;
      }
    },
    async onTypingFinish({ resultType, answers }) {
      if (this.typingDialogTask) {
        await this.executeSegmentTask(this.typingDialogTask);
        this.showTypingDialog = false;
        this.typingDialogTask = null;
        this.typingDialogAnswers = null;
        this.loadTaskStats();
      }
    },
    async onSurveyFinish({ questionnaireId, answers }) {
      if (this.surveyDialogTask) {
        await this.executeSegmentTask(this.surveyDialogTask);
        this.showSurveyDialog = false;
        this.surveyDialogTask = null;
        this.loadTaskStats();
      }
    },
    async executeSegmentTask(task) {
      try {
        await executeTask('accept', task.campaign_event_id, task.physician_id, this.userId, localStorage.getItem('user_name') || '', localStorage.getItem('territory_id') || '', this.roleId);
        Toast.success(this.$t('task_executed'));
      } catch (err) {
        console.error('Failed to execute segment task:', err);
        Toast.fail(this.$t('execute_failed') || 'Task Execution Failed');
      }
    },
    async fetchRoleOptions() {
      try {
        const loginRolesStr = getLocalStorage('login_roles');
        let roles = [];
        if (loginRolesStr) {
          roles = JSON.parse(loginRolesStr);
        }

        if (roles && roles.length) {
          const targetRoles = ['销售', 'GBA', 'GA', 'GA管理员'];
          this.roleActions = roles
            .filter(item => targetRoles.includes(item.role_name))
            .sort((a, b) => (Number(a.role_id) > Number(b.role_id) ? 1 : -1))
            .map(item => ({
              name: item.role_name,
              role_id: item.role_id,
              color: item.role_name === this.currentRole || (this.currentRole === 'MR' && item.role_name === '销售') ? '#aa341b' : undefined
            }));
          console.log('Fetched roles:', this.roleActions);
        }
      } catch (error) {
        console.error('Fetch roles failed', error);
      }
    },
    async onSelectRole(action) {
      if (this.currentRole === action.name) {
        this.showRoleSelector = false;
        return;
      }

      Toast.loading({
        message: '切换中...',
        forbidClick: true,
      });

      try {
        const res = await switchRole(action.role_id);
        if (res && res.isSuccess && res.data) {
          const { token, seed, user } = res.data;
          setToken({ token, seed });

          if (user) {
            setLocalStorage('user_id', user.user_id);
            setLocalStorage('user_name', user.user_name);
            setLocalStorage('territory_id', user.territory_id);
            setLocalStorage('territory_type', user.territory_type);
            setLocalStorage('role_id', user.role_id);
            setLocalStorage('role_name', user.role_name);
            setLocalStorage('enable_ai', resolveEnableAI(user.enable_ai, user.role_id, user.role_name) ? '1' : '0');
          }

          this.currentRole = action.name;

          // Update action sheet colors
          this.roleActions.forEach(item => {
            item.color = item.name === action.name ? '#aa341b' : undefined;
          });

          Toast.success(`已切换为\n ${action.name} 身份`);

          // 重新加载页面以应用新的权限和Token
          if (user && user.role_name === 'GA管理员') {
            window.location.href = '/ga-admin/overview';
          } else if (isGBA()) {
            window.location.href = '/gba/overview';
          } else {
            window.location.href = isEnableAI() ? '/hcp/overview' : '/hcp/list';
          }
        } else {
          Toast.fail(res.message || '切换身份失败');
        }
      } catch (error) {
        console.error(error);
        Toast.fail('切换身份失败');
      }
    },
    goToTasks(event = null) {
      const path = isGBA() ? '/gba/task' : '/hcp/task';
      const campaignEventId = event && event.campaign_event_id ? event.campaign_event_id : '';

      this.$router.push({
        path,
        query: campaignEventId ? { campaign_event_id: campaignEventId } : { clear_event: '1' }
      });
    },
    goToDoctorDetail(task = null) {
      let targetId = null;
      if (task && typeof task === 'object' && task.physician_id) {
        targetId = task.physician_id;
      } else if (typeof task === 'string' || typeof task === 'number') {
        targetId = task;
      }

      if (targetId) {
        const path = isGBA() ? '/gba/detail' : '/hcp/detail';
        this.$router.push({
          path: path,
          query: { physician_id: targetId }
        });
      }
    },
    planVisit(task) {
      if (task && task.physician_id) {
        const path = isGBA() ? '/gba/visit-create' : '/hcp/visit-create';
        this.$router.push({
          path: path,
          query: {
            physician_id: task.physician_id
          }
        });
      }
    },
    askAIHelper(doctorInfo) {
      if (doctorInfo && doctorInfo.name && doctorInfo.hospital) {
        const lang = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'zh';
        const actionType = doctorInfo.suggestion_type || doctorInfo.action_type || '';
        const isContentPush = actionType === 'content_push';
        const contentId = doctorInfo.recommended_content_id || doctorInfo.content_id || '';
        const originalTitle = doctorInfo.original_content_title || doctorInfo.recommended_content_title || doctorInfo.content_title || '';
        const topic = doctorInfo.recommended_topic || originalTitle || 'approved online content';
        if (lang.toLowerCase() === 'en') {
          this.$root.$emit(
            'open-ai-chat',
            isContentPush
              ? `Please help me prepare an approved content push for ${doctorInfo.name} (HcpID: ${doctorInfo.id}). Content ID: ${contentId || 'N/A'}. Original article title: ${originalTitle || 'N/A'}. Topic: ${topic}`
              : `Please help me prepare the next best action for ${doctorInfo.name} (HcpID: ${doctorInfo.id})`,
            true
          );
        } else {
          this.$root.$emit(
            'open-ai-chat',
            isContentPush
              ? `帮我准备给${doctorInfo.name}（医生ID：${doctorInfo.id}）的合规内容推送。内容ID：${contentId || '无'}。文章原始标题：${originalTitle || '无'}。主题：${topic || '系统内已审批线上内容'}`
              : `帮我准备${doctorInfo.name}（医生ID：${doctorInfo.id}）的下一步行动`,
            true
          );
        }
      }
    },
    askInsights() {
      if (this.recentUpdatesLoading) return;
      this.openRecentUpdates();
    },
    goToCreateVisit(payload) {
      const pId = payload && typeof payload === 'object' ? payload.physician_id : payload;
      const today = moment().format('YYYY/MM/DD');
      if (isGBA()) {
        this.$router.push({
          path: '/gba/visit-create',
          query: {
            physician_id: pId,
            visit_date: today,
            visit_time: '上午',
            visit_type: '面对面拜访',
            visit_goal: 'NRDL'
          }
        });
      } else {
        this.$router.push({
          path: '/hcp/visit-create',
          query: {
            physician_id: pId,
            visit_date: today,
            visit_time: '上午',
            visit_type: '面对面拜访',
            visit_goal: '产品信息传递'
          }
        });
      }
    },
    goToSendContent(payload) {
      const pId = payload && typeof payload === 'object' ? payload.physician_id : payload;
      const pName = payload && typeof payload === 'object' ? payload.physician_name || payload.name : '';
      const path = isGBA() ? '/gba/content' : '/hcp/content';
      this.$router.push({
        path,
        query: {
          physician_id: pId || '',
          physician_name: pName || '',
          nba_suggestion_id: payload && typeof payload === 'object' ? payload.nba_suggestion_id || '' : '',
          recommended_topic: payload && typeof payload === 'object' ? payload.recommended_topic || '' : '',
          recommended_content_id: payload && typeof payload === 'object' ? payload.recommended_content_id || '' : '',
          recommended_content_title: payload && typeof payload === 'object' ? payload.recommended_content_title || '' : ''
        }
      });
    },
    switchLanguage(lang) {
      if (this.currentLanguage === lang) return;
      this.currentLanguage = lang;
      this.$i18n.locale = lang;
      setLocalStorage('language', lang); // Use imported setLocalStorage
      // Refresh to ensure all components are re-rendered with new locale if needed,
      // but usually vue-i18n handles reactive changes.
      // Sometimes just changing locale is enough.
      // However, some libraries might need a refresh.
      // For now, just change locale.
      Toast.success(lang === 'ZH' ? '已切换至中文' : 'Switched to English');
    }
  },
};
</script>

<style lang="scss" scoped>
.overview-container {
  background-color: #f2f5f9;
  background: linear-gradient(175deg, #e6f0ff 10%, #f0e9f4 30%, #f2f5f9 50%);
  min-height: 100vh;
  font-family: PingFang SC, sans-serif;
  text-align: left;
}

.header-bg {
  background: linear-gradient(165deg, #4d50e9 10%, #b84fd7 30%, #aa341b 70%);
  height: 170px;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  padding-top: 30px;
  position: relative;
  overflow: hidden;

  .decorative-circle {
    position: absolute;
    border-radius: 50%;
    filter: blur(30px);
    z-index: 0;
  }

  .circle-1 {
    width: 220px;
    height: 220px;
    background: rgba(255, 255, 255, 0.25);
    top: -80px;
    left: -60px;
  }

  .circle-2 {
    width: 200px;
    height: 200px;
    background: rgba(255, 255, 255, 0.2);
    bottom: -50px;
    right: -40px;
  }

  .header-content {
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    font-size: 20px;
    font-weight: 500;
    position: relative;
    z-index: 1;
    padding: 0 20px;

    .user-info {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;

      .avatar {
        width: 40px;
        height: 40px;
        background: #ecf0fd;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        left: 0;

        img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
        }
      }
    }

    .role-switcher {
      position: absolute;
      right: 20px;
      display: flex;
      align-items: center;
      background: rgba(255, 255, 255, 0.2);
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 12px;
      cursor: pointer;

      .role-text {
        margin-right: 4px;
        font-weight: bold;
      }
    }
  }
}

.section-card {
  margin-bottom: 24px;

  .section-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;

    .van-icon {
      margin-right: 8px;
    }

    .header-text {
      .main-text {
        font-size: 16px;
        font-weight: bold;
        color: #333;
      }
    }
  }
}

.priority-card, .strategy-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.priority-card {
  .priority-tag {
    background-color: #fceceb;
    color: #aa341b;
    font-size: 13px;
    // font-weight: bold;
    padding: 10px 16px;
    display: flex;
    align-items: center;
  }

  .card-content {
    padding: 20px 16px;
    border-bottom: 1px solid #f2f5f9;

    .card-title-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      .card-title {
        font-size: 18px;
        font-weight: bold;
        color: #333;
      }
    }

    .card-subtitle {
      font-size: 15px;
      color: #333;
      margin-bottom: 16px;
    }

    .reason-list {
      padding-left: 16px;
      margin: 0 0 20px 0;

      li {
        font-size: 13px;
        color: #666;
        margin-bottom: 6px;
        list-style-type: disc;
      }
    }

    .action-row {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .action-left {
        display: flex;
        align-items: center;
        font-size: 12px;
        color: #666;

        .van-icon {
          margin-right: 4px;
        }
      }

      .action-btn {
        padding: 0 20px;
      }
    }
  }
}

.summary-card {
  background: white;
  border-radius: 16px;
  padding: 16px 16px 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  .title-area {
    display: flex;
    align-items: center;

    .icon-main {
      margin-right: 6px;
      font-weight: bold;
    }

    .main-title {
      font-size: 18px;
      font-weight: bold;
      color: #333;
    }
  }

  .stats-area {
    font-size: 14px;

    .num {
      font-weight: bold;
      font-size: 18px;
      color: #333;
    }

    .label {
      font-size: 12px;
      color: #666;
      margin-left: 2px;
    }

    .divider {
      color: #eee;
      margin: 0 8px;
    }
  }
}

.summary-desc {
  font-size: 13px;
  color: #666;
  margin-bottom: 12px;
}

.action-row {
  display: flex;
  align-items: center;
  gap: 12px;

  .progress-track {
    flex: 1;
    height: 32px;
    background: linear-gradient(90deg, #fceceb 0%, #ffffff 100%);
    border-radius: 16px;
    position: relative;
    overflow: hidden;
    border: 1px solid #fceceb;

    .progress-bar {
      height: 100%;
      background: linear-gradient(90deg, #ff8e8e 0%, #aa341b 100%);
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;

      .progress-text {
        color: white;
        font-size: 14px;
        font-weight: bold;
        position: absolute;
        right: 12px;
      }
    }
  }

  .go-btn {
    background: white;
    color: #aa341b;
    border: 1.5px solid #aa341b;
    padding: 0 16px;
    height: 30px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
    white-space: nowrap;

    .van-icon {
      margin-left: 4px;
      font-size: 12px;
    }
  }
}

.section-header-simple {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #333;
  font-weight: 500;
  margin-bottom: 12px;
  padding-left: 4px;
}

.priority-block-content {
  /* Previously .priority-new-card - styles moved to parent .summary-card */
  padding-top: 4px;

  .card-top-tags {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .tag-red {
      background: #d1402b;
      color: white;
      font-size: 11px;
      padding: 2px 8px;
      border-radius: 4px;
      font-weight: bold;
    }

    .tag-info {
      font-size: 12px;
      color: #999;
      display: flex;
      align-items: center;

      .van-icon {
        margin-left: 2px;
      }
    }
  }

  .card-main-title {
    font-size: 16px;
    font-weight: bold;
    color: #333;
    line-height: 1.5;
    margin-bottom: 16px;
  }

  .doctor-action-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .doctor-info-left {
      display: flex;
      align-items: center;
      flex: 1;
      margin-right: 8px;

      .avatar-circle {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: #f0f0f0;
        margin-right: 12px;
        overflow: hidden;
        flex-shrink: 0;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .text-info {
        flex: 1;

        .name-line {
          margin-bottom: 2px;
          .name {
            font-size: 15px;
            font-weight: bold;
            color: #333;
          }
        }

        .desc-line {
          font-size: 11px;
          color: #666;
          margin-bottom: 1px;
        }

        .hospital-line {
          font-size: 10px;
          color: #999;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 150px;
        }
      }
    }

    .action-right {
      flex-shrink: 0;
      // .finish-btn {
      //   padding: 0 16px;
      //   height: 28px;
      //   line-height: 26px;
      //   font-size: 13px;
      // }

      .cp-share {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex-shrink: 0;

        .share-icon-bg {
            width: 24px;
            height: 24px;
            background: linear-gradient(165deg, rgb(210, 86, 61), rgb(170, 52, 27));
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 2px;
          }

        .share-text {
          font-size: 10px;
        }
      }
    }
  }

  .card-footer-link {
    border-top: 1px solid #f5f5f5;
    padding-top: 12px;
    text-align: center;
    font-size: 12px;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: center;

    .van-icon {
      margin-left: 4px;
      color: #999;
    }
  }
}

.strategy-card {
  padding: 16px 16px 0 16px;

  .system-recommendation-banner {
    background: linear-gradient(90deg, #fff5f5 0%, #fffbfb 100%);
    color: #8c4e4e;
    font-size: 12px;
    font-weight: 500;
    padding: 12px 16px 12px 16px;
    margin: -16px -16px 16px -16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0 0 50% 50% / 0 0 15px 15px;
    position: relative;
    z-index: 0;

    .banner-text {
      letter-spacing: 0.5px;
      font-weight: 400;
    }
  }

  .card-top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .doctor-info-simple {
      display: flex;
      align-items: center;
      .avatar-wrapper {
        width: 44px;
        height: 44px;
        background: #f0f0f0;
        border-radius: 50%;
        margin-right: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: hidden;
        .avatar-img {
          width: 50px;
          height: 50px;
          object-fit: cover;
          opacity: 0.5;
        }
      }
      .info-text {
        .name {
          font-size: 16px;
          font-weight: bold;
          color: #333;
        }
        .details {
          font-size: 12px;
          color: #666;
          .dept { margin-right: 4px; }
          .hospital { display: block; color: #999; font-size: 11px; }
        }
      }
    }

    .ai-helper-btn {
      background: linear-gradient(#fff, #fff) padding-box, linear-gradient(165deg, #4d50e9 0%, #b84fd7 50%, #aa341b 100%) border-box;
      border: 1.5px solid transparent;
      padding: 6px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
      margin-right: 10px;
      display: inline-flex; /* Ensure it wraps content tightly */

      .ai-gradient-text {
        display: flex;
        align-items: center;
        background: linear-gradient(165deg, #4d50e9 0%, #b84fd7 50%, #aa341b 100%);
        background-clip: text;
        -webkit-background-clip: text;
        color: transparent;
      }
    }
  }

  .info-content-box {
    background: #fcfcfc;
    border: 1px solid #f6f6f6;
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 16px;

    .info-row-flex {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 14px;

      .info-left-group {
        .info-item-main {
           display: flex;
           align-items: center;
           font-size: 13px;
           color: #333;
           margin-bottom: 2px;
           .van-icon { margin-right: 4px; }
        }
        .sub-alert-text {
          font-size: 10px;
          color: #d1402b;
          margin-left: 20px;
        }
      }

      .v-divider {
        width: 1px;
        height: 24px;
        background: #eee;
        margin: 0 8px;
        align-self: center;
      }

      .info-mid-item {
        display: flex;
        align-items: center;
        font-size: 12px;
        color: #333;
        .van-icon { margin-right: 4px; }
      }
    }

    .create-plan-row {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 12px;

      .create-plan-btn {
        border: 1.5px solid #aa341b;
        color: #aa341b;
        background: white;
        padding: 6px 12px;
        border-radius: 16px;
        font-size: 13px;
        display: flex;
        align-items: center;
        white-space: nowrap;

        .van-icon {
            margin-left: 2px;
            font-size: 10px;
        }
      }
    }

    .summary-section {
       border-top: 1px dashed #eee;
       padding-top: 10px;
       padding-bottom: 10px;

       .summary-title {
         font-size: 13px;
         font-weight: bold;
         color: #333;
         margin-bottom: 4px;
       }
       .summary-content {
         font-size: 12px;
         color: #666;
         line-height: 1.4;
       }
    }
  }

  .card-footer {
    border-top: 1px solid #ebedf0;
    margin: 0 -16px;
    padding: 12px 0;
    display: flex;

    .footer-btn {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 12px;
      color: #999;
      cursor: pointer;

      .van-icon {
        margin-right: 6px;
      }

      &:first-child {
        border-right: 1px solid #ebedf0;
      }
    }
  }
}

.middle-section {
  display: flex;
  margin: 0 0 24px 0;
  gap: 12px;

  .info-card {
    background: white;
    border-radius: 16px;
    padding: 15px;
    flex: 1;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 15px;
      font-weight: bold;
      margin-bottom: 15px;
      color: #333;
    }
  }

  .suggestion-card {
    .kpi-hint {
      font-size: 11px;
      color: #aab0bb;
      margin: -8px 0 12px;
      line-height: 1.4;
    }

    .kpi-bullet-list {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
        font-size: 12px;
        color: #555;
        line-height: 1.5;

        &::before {
          content: '';
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: 2px solid #ccc;
          display: inline-block;
          margin-right: 8px;
          flex-shrink: 0;
        }
      }

      .kpi-item-green::before  { border-color: #4db889; }
      .kpi-item-blue::before   { border-color: #5b8de0; }
      .kpi-item-orange::before { border-color: #f08060; }
    }
  }

  .insight-card {
    .insight-loading {
      display: flex;
      align-items: center;
      padding: 6px 0;
    }

    .ai-bubble {
      background: #f2f3f5;
      padding: 8px 12px;
      border-radius: 0 12px 12px 12px;
      display: inline-flex;
      align-items: center;
    }

    .insight-list {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        display: flex;
        align-items: flex-start;
        margin-bottom: 10px;
        font-size: 12px;
        color: #666;
        line-height: 1.5;

        .bullet {
          color: #ccc;
          margin-right: 6px;
          transform: scale(0.8);
          margin-top: 2px;
        }
      }
    }

    .typing-indicator {
      display: inline-flex;
      align-items: center;
      height: 20px;

      .dot {
        width: 5px;
        height: 5px;
        margin: 0 2px;
        background-color: #999;
        border-radius: 50%;
        animation: typing 1.4s infinite ease-in-out both;

        &:nth-child(1) { animation-delay: -0.32s; }
        &:nth-child(2) { animation-delay: -0.16s; }
      }
    }

    @keyframes typing {
      0%, 80%, 100% { transform: scale(0); }
      40% { transform: scale(1); }
    }
  }
}

.visit-section {
  margin: 20px 15px;

  .section-title {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 15px;
    color: #333;
    text-align: left;
  }

  .visit-list {
    .visit-card {
      background: white;
      border-radius: 16px;
      padding: 20px;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      box-shadow: 0 2px 8px rgba(0,0,0,0.05);

      .doctor-avatar-wrapper {
        width: 52px;
        height: 52px;
        margin-right: 15px;
        border-radius: 50%;
        background: linear-gradient(165deg, #dce1f0, #f6eff1);
        overflow: hidden;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-shrink: 0;
      }

      .doctor-avatar-img {
        width: 55px;
        height: 55px;
        object-fit: cover;
        opacity: 0.5;
      }

      .doctor-info {
        flex: 1;

        .name-row {
          font-size: 18px;
          font-weight: bold;
          color: #333;
          margin-bottom: 6px;
        }

        .title-row {
          font-size: 13px;
          color: #666;
          margin-bottom: 4px;
        }

        .hospital-row {
          font-size: 13px;
          color: #999;
          margin-bottom: 4px;
        }

        .date-row {
          font-size: 13px;
          color: #999;
        }
      }

      .action-btn {
        flex-shrink: 0;
      }
    }
  }
}

.user-info-card {
  text-align: center;

  .info-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 20px;

    .avatar-large {
      width: 80px;
      height: 80px;
      background-color: #aa341b;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 12px;
      box-shadow: 0 4px 10px rgba(0,0,0,0.1);
    }

    .info-title {
      font-size: 18px;
      font-weight: bold;
      color: #333;
    }
  }

  .info-content {
    background: #f9f9f9;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;

    .info-item {
      display: flex;
      justify-content: space-between;
      padding: 10px 0;
      border-bottom: 1px dashed #eee;

      &:last-child {
        border-bottom: none;
      }

      .label {
        color: #666;
        font-size: 14px;
      }

      .value {
        color: #333;
        font-weight: 500;
        font-size: 14px;
      }

      .language-switch {
        display: flex;
        align-items: center;

        .lang-option {
          cursor: pointer;
          color: #999;
          font-size: 14px;
          &.active {
            color: #aa341b;
            font-weight: bold;
          }
        }

        .divider {
          margin: 0 8px;
          color: #eee;
        }
      }
    }
  }
}
</style>
